import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, SlidersHorizontal, X, FileText } from 'lucide-react';
type PreShiftStatus = 'Completed' | 'No Content' | 'Workers Signing';
interface PreShift {
  id: string;
  fileType: 'pdf' | 'missing';
  fileName: string;
  site: string;
  subName: string;
  date: string;
  status: PreShiftStatus;
}
const SITES = ['1760 3rd Ave', '1760 Jerome Ave', 'Court Square LIC', '99 Claremont Ave', '251 Douglass Street', '450 Park Avenue South', '520 Fifth Avenue'];
const SUBS = ['Aurora Paint Corp.', 'Skytrac', 'Flushing Iron Weld', 'Sector Security', 'Luxintec', 'V10 Construction', 'Kitchens Of USA', 'Otis Elevator Company', 'Sunkeeper Solar', 'Architectural Flooring Resource Inc', 'Nu-Way Heating & Cooling Inc', 'Bedrock Plumbing & Heating Inc.', 'Emerald Tile & Marble Inc.', 'Acon Construction', 'Rainier Restoration LLC', 'Concept Mechanical Plumbing Corp', 'Eastern Design Tile & Marble', 'SteelCo Inc', 'Broadway Construction Group', 'Clear Path Contracting LLC', 'AV Mechanic LLC', 'Metro Electric Corp', 'Pinnacle Drywall LLC', 'Summit Plumbing Co.', 'Atlas Fire Protection', 'Citywide HVAC Services', 'Premier Concrete Inc.', 'Liberty Scaffolding LLC', 'Apex Roofing Systems', 'Guardian Safety Services'];
// Generate a realistic dataset
// We want: ~100 total, 69 incomplete within last 7 days, some completed, some older
function generateMockData(): PreShift[] {
  const data: PreShift[] = [];
  let id = 1;
  // Helper to create a pre-shift
  const add = (date: string, status: PreShiftStatus, subIdx: number, siteIdx: number) => {
    const isCompleted = status === 'Completed';
    data.push({
      id: String(id++),
      fileType: isCompleted ? 'pdf' : status === 'Workers Signing' ? 'pdf' : 'missing',
      fileName: isCompleted ? 'Signed Pre-Shift' : '',
      site: SITES[siteIdx % SITES.length],
      subName: SUBS[subIdx % SUBS.length],
      date,
      status
    });
  };
  // --- LAST 7 DAYS: 69 incomplete + 9 completed = 78 total ---
  // 03/15/2026 (today) — 12 incomplete, 2 completed
  for (let i = 0; i < 7; i++) add('03/15/2026', 'No Content', i, i);
  for (let i = 7; i < 12; i++) add('03/15/2026', 'Workers Signing', i, i % 7);
  add('03/15/2026', 'Completed', 12, 0);
  add('03/15/2026', 'Completed', 13, 1);
  // 03/14/2026 — 11 incomplete, 1 completed
  for (let i = 0; i < 6; i++) add('03/14/2026', 'No Content', i + 3, i);
  for (let i = 0; i < 5; i++) add('03/14/2026', 'Workers Signing', i + 10, i + 2);
  add('03/14/2026', 'Completed', 14, 3);
  // 03/13/2026 — 12 incomplete, 2 completed
  for (let i = 0; i < 5; i++) add('03/13/2026', 'No Content', i + 5, i + 1);
  for (let i = 0; i < 7; i++) add('03/13/2026', 'Workers Signing', i + 15, i);
  add('03/13/2026', 'Completed', 20, 4);
  add('03/13/2026', 'Completed', 21, 5);
  // 03/12/2026 — 10 incomplete, 1 completed
  for (let i = 0; i < 4; i++) add('03/12/2026', 'No Content', i + 8, i + 3);
  for (let i = 0; i < 6; i++) add('03/12/2026', 'Workers Signing', i + 20, i + 1);
  add('03/12/2026', 'Completed', 25, 6);
  // 03/11/2026 — 9 incomplete, 1 completed
  for (let i = 0; i < 3; i++) add('03/11/2026', 'No Content', i + 12, i + 4);
  for (let i = 0; i < 6; i++) add('03/11/2026', 'Workers Signing', i + 24, i + 2);
  add('03/11/2026', 'Completed', 28, 0);
  // 03/10/2026 — 8 incomplete, 1 completed
  for (let i = 0; i < 3; i++) add('03/10/2026', 'No Content', i + 16, i + 5);
  for (let i = 0; i < 5; i++) add('03/10/2026', 'Workers Signing', i + 2, i + 3);
  add('03/10/2026', 'Completed', 7, 2);
  // 03/09/2026 — 7 incomplete, 1 completed
  for (let i = 0; i < 3; i++) add('03/09/2026', 'No Content', i + 19, i + 6);
  for (let i = 0; i < 4; i++) add('03/09/2026', 'Workers Signing', i + 6, i + 4);
  add('03/09/2026', 'Completed', 10, 1);
  // --- OLDER DATA (before last 7 days) — adds to total count ---
  // 03/08/2026 — 5 completed, 3 incomplete
  for (let i = 0; i < 5; i++) add('03/08/2026', 'Completed', i + 22, i);
  for (let i = 0; i < 3; i++) add('03/08/2026', 'No Content', i + 27, i + 3);
  // 03/07/2026 — 4 completed, 2 incomplete
  for (let i = 0; i < 4; i++) add('03/07/2026', 'Completed', i + 10, i + 1);
  for (let i = 0; i < 2; i++) add('03/07/2026', 'Workers Signing', i + 14, i + 5);
  // 03/06/2026 — 6 completed
  for (let i = 0; i < 6; i++) add('03/06/2026', 'Completed', i + 18, i + 2);
  // 03/05/2026 — 5 completed, 1 incomplete
  for (let i = 0; i < 5; i++) add('03/05/2026', 'Completed', i + 4, i + 4);
  add('03/05/2026', 'No Content', 29, 6);
  return data;
}
const MOCK_PRESHIFTS = generateMockData();
export function PreShiftPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  // Filter states
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [periodLabel, setPeriodLabel] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<{
    start: string | null;
    end: string | null;
  }>({
    start: null,
    end: null
  });
  // Initialize filters from URL
  useEffect(() => {
    const period = searchParams.get('period');
    const status = searchParams.get('status');
    if (status === 'incomplete') {
      setStatusFilter('Incomplete');
    } else {
      setStatusFilter(null);
    }
    if (period === 'today') {
      setPeriodLabel('Today');
      setDateRange({
        start: '03/15/2026',
        end: '03/15/2026'
      });
    } else if (period === 'week') {
      setPeriodLabel('Earlier this week');
      setDateRange({
        start: '03/09/2026',
        end: '03/15/2026'
      });
    } else {
      setPeriodLabel(null);
      setDateRange({
        start: null,
        end: null
      });
    }
  }, [searchParams]);
  const clearAllFilters = () => {
    setStatusFilter(null);
    setPeriodLabel(null);
    setDateRange({
      start: null,
      end: null
    });
    setSearchParams({});
  };
  const removeStatusFilter = () => {
    setStatusFilter(null);
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('status');
    setSearchParams(newParams);
  };
  const removeDateFilter = () => {
    setPeriodLabel(null);
    setDateRange({
      start: null,
      end: null
    });
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('period');
    setSearchParams(newParams);
  };
  // Apply filters
  const filteredData = useMemo(() => {
    return MOCK_PRESHIFTS.filter(item => {
      // Search filter
      if (searchQuery && !item.subName.toLowerCase().includes(searchQuery.toLowerCase()) && !item.site.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      // Status filter
      if (statusFilter === 'Incomplete') {
        if (item.status === 'Completed') return false;
      } else if (statusFilter && item.status !== statusFilter) {
        return false;
      }
      // Date filter
      if (dateRange.start && item.date < dateRange.start) return false;
      if (dateRange.end && item.date > dateRange.end) return false;
      return true;
    });
  }, [searchQuery, statusFilter, dateRange]);
  const hasActiveFilters = statusFilter !== null || dateRange.start !== null;
  return <div className="min-h-screen bg-[#fafafa] font-sans flex flex-col" data-id="element-4337">
      {/* Page Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-4" data-id="element-4338">
        <div className="flex items-center gap-2" data-id="element-4339">
          <div className="text-blue-600 font-bold text-2xl tracking-tighter flex items-center" data-id="element-4340">
            CHST
            <div className="w-6 h-6 border-4 border-blue-600 rounded-full mx-0.5 relative" data-id="element-4341">
              <div className="absolute inset-0 m-auto w-2 h-2 bg-blue-600 rounded-full" data-id="element-4342"></div>
            </div>
          </div>
          <div className="text-[10px] font-bold text-blue-600 tracking-widest absolute mt-8 ml-1" data-id="element-4343">
            L D E R S
          </div>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 ml-4" data-id="element-4344">Pre-Shift</h1>
      </div>

      <main className="flex-1 p-6 max-w-[1600px] mx-auto w-full" data-id="element-4345">
        {/* Controls Row */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6" data-id="element-4346">
          <h2 className="text-xl text-gray-700" data-id="element-4347">
            Found{' '}
            <span className="font-semibold text-gray-900" data-id="element-4348">
              {filteredData.length}
            </span>{' '}
            Pre-Shifts
          </h2>

          <div className="flex items-center gap-3" data-id="element-4349">
            <div className="relative" data-id="element-4350">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-4351" />
              <input type="text" placeholder="Search" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm w-64 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-4352" />
            </div>
            <button className="relative flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 bg-white transition-colors shadow-sm" data-id="element-4353">
              <SlidersHorizontal size={16} data-id="element-4354" />
              Filters
              {hasActiveFilters && <span className="absolute top-0 right-0 -mt-1 -mr-1 w-2.5 h-2.5 bg-emerald-500 border-2 border-white rounded-full" data-id="element-4355"></span>}
            </button>
          </div>
        </div>

        {/* Active Filters Row */}
        {hasActiveFilters && <div className="flex items-center flex-wrap gap-2 mb-6" data-id="element-4356">
            {statusFilter && <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 text-white rounded-full text-sm" data-id="element-4357">
                <span className="text-gray-300" data-id="element-4358">Status:</span>
                <span className="font-medium" data-id="element-4359">{statusFilter}</span>
                <button onClick={removeStatusFilter} className="ml-1 text-gray-400 hover:text-white transition-colors" data-id="element-4360">
                  <X size={14} data-id="element-4361" />
                </button>
              </div>}

            {periodLabel && <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 text-white rounded-full text-sm" data-id="element-4362">
                <span className="text-gray-300" data-id="element-4363">Period:</span>
                <span className="font-medium" data-id="element-4364">{periodLabel}</span>
                <span className="text-gray-400 text-xs" data-id="element-4365">
                  ({dateRange.start} – {dateRange.end})
                </span>
                <button onClick={removeDateFilter} className="ml-1 text-gray-400 hover:text-white transition-colors" data-id="element-4366">
                  <X size={14} data-id="element-4367" />
                </button>
              </div>}

            <button onClick={clearAllFilters} className="text-sm text-gray-500 hover:text-gray-900 underline underline-offset-2 ml-2 transition-colors" data-id="element-4368">
              Clear all
            </button>
          </div>}

        {/* Table */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden" data-id="element-4369">
          <div className="overflow-x-auto" data-id="element-4370">
            <table className="w-full text-left text-sm" data-id="element-4371">
              <thead className="bg-gray-50 border-b border-gray-200 text-gray-500 font-semibold text-xs tracking-wider" data-id="element-4372">
                <tr data-id="element-4373">
                  <th className="px-6 py-4" data-id="element-4374">FILE</th>
                  <th className="px-6 py-4" data-id="element-4375">SITE</th>
                  <th className="px-6 py-4" data-id="element-4376">SUB NAME</th>
                  <th className="px-6 py-4" data-id="element-4377">DATE</th>
                  <th className="px-6 py-4" data-id="element-4378">STATUS</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-4379">
                {filteredData.map(row => <tr key={row.id} className="hover:bg-gray-50 transition-colors" data-id="element-4380">
                    <td className="px-6 py-4" data-id="element-4381">
                      <div className="flex items-center gap-3" data-id="element-4382">
                        {row.fileType === 'pdf' ? <div className="relative" data-id="element-4383">
                            <FileText size={24} className="text-gray-300" data-id="element-4384" />
                            <div className="absolute bottom-0 left-0 bg-red-600 text-white text-[8px] font-bold px-1 rounded-sm" data-id="element-4385">
                              PDF
                            </div>
                          </div> : <div className="relative" data-id="element-4386">
                            <FileText size={24} className="text-gray-200" data-id="element-4387" />
                            <div className="absolute bottom-0 left-0 bg-gray-500 text-white text-[8px] font-bold px-1 rounded-sm" data-id="element-4388">
                              ?
                            </div>
                          </div>}
                        <span className="text-gray-900" data-id="element-4389">{row.fileName}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-900" data-id="element-4390">{row.site}</td>
                    <td className="px-6 py-4 text-gray-900" data-id="element-4391">{row.subName}</td>
                    <td className="px-6 py-4 text-gray-900" data-id="element-4392">{row.date}</td>
                    <td className="px-6 py-4" data-id="element-4393">
                      {row.status === 'Completed' && <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-emerald-100 text-emerald-700" data-id="element-4394">
                          Completed
                        </span>}
                      {row.status === 'No Content' && <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-700" data-id="element-4395">
                          No Content
                        </span>}
                      {row.status === 'Workers Signing' && <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-amber-100 text-amber-700" data-id="element-4396">
                          Workers Signing
                        </span>}
                    </td>
                  </tr>)}
                {filteredData.length === 0 && <tr data-id="element-4397">
                    <td colSpan={5} className="px-6 py-12 text-center text-gray-500" data-id="element-4398">
                      No Pre-Shifts found matching your criteria.
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>;
}